import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

interface BatchSetLaborTeamModalProps {
    open: boolean;
    accounts: Array<{ accountId: string; name: string }>;
    recentLaborTeamOptions: string[];
    loading?: boolean;
    onCancel: () => void;
    onSave: (laborTeamName: string) => void;
}

const BatchSetLaborTeamModal: React.FC<BatchSetLaborTeamModalProps> = ({
    open,
    accounts,
    recentLaborTeamOptions,
    loading = false,
    onCancel,
    onSave,
}) => {
    const { t } = useTranslation();
    const [laborTeamName, setLaborTeamName] = useState('');
    const [showRecentTags, setShowRecentTags] = useState(true);

    useEffect(() => {
        if (open) {
            setLaborTeamName('');
            setShowRecentTags(true);
        }
    }, [open, accounts]);

    const handleSave = () => {
        const trimmed = laborTeamName.trim();
        if (!trimmed) {
            return;
        }
        onSave(trimmed);
    };

    const handleClear = () => {
        setShowRecentTags(false);
    };

    const selectedNames = accounts.map((item) => item.name).join(', ');

    return (
        <Modal
            title={t('wms.sortingLaborTeam.modal.title')}
            open={open}
            onCancel={onCancel}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t('wms.sortingLaborTeam.modal.cancel')}
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    loading={loading}
                    onClick={handleSave}
                    disabled={!laborTeamName.trim()}
                >
                    {t('wms.sortingLaborTeam.modal.save')}
                </Button>,
            ]}
        >
            <div className="mb-4 text-gray-600">
                {t('wms.sortingLaborTeam.modal.selectedSummary', { count: accounts.length, names: selectedNames })}
            </div>
            <div className="mb-2 font-medium">{t('wms.sortingLaborTeam.modal.laborTeamName')}</div>
            <Input
                value={laborTeamName}
                onChange={(e) => setLaborTeamName(e.target.value)}
                placeholder={t('wms.sortingLaborTeam.modal.placeholder') as string}
                maxLength={64}
            />
            {showRecentTags && recentLaborTeamOptions.length > 0 && (
                <div className="mt-4">
                    <div className="mb-2 text-sm text-gray-500">
                        {t('wms.sortingLaborTeam.modal.recentTeams')}
                    </div>
                    <Space size={[10, 10]} wrap>
                        {recentLaborTeamOptions.map((name) => (
                            <Tag
                                key={name}
                                color={laborTeamName === name ? 'blue' : 'default'}
                                className="cursor-pointer rounded-md px-3 py-1 text-sm leading-5 m-0"
                                onClick={() => setLaborTeamName(name)}
                            >
                                {name}
                            </Tag>
                        ))}
                    </Space>
                    <div className="mt-2">
                        <Button type="link" size="small" className="!h-auto !p-0 text-xs text-gray-400" onClick={handleClear}>
                            {t('wms.sortingLaborTeam.modal.clear')}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default BatchSetLaborTeamModal;
